import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { PageHeader, Spacer } from "../MasterCss";
import Button from "../Button";
import * as SC from "../GameScreen/GameScreen.style";
import ResponsiveSvg from "../ResponsiveSvg";

const STORAGE_KEY = "wasteSortingLeaderboard";

const loadScores = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const LeaderboardWrapper = styled.div`
  background-image: linear-gradient(#21b2d3, #7abefd);
  min-height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2vh 2vw;
  box-sizing: border-box;
  overflow-x: hidden;
  position: relative;
`;

const ScrollableContent = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 4vh;
  position: relative;
  z-index: 2;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 30px;
  border: 2px solid #08345c;
  padding: 2rem 1.5rem;
  width: 92%;
  max-width: 650px;
  margin: 2vh auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);
  box-sizing: border-box;
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    width: 95%;
  }
  @media (max-width: 360px) {
    padding: 1rem 0.8rem;
    width: 97%;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const Tab = styled.button`
  padding: 0.6rem 1.8rem;
  border-radius: 50px;
  border: 2px solid #08345c;
  background: ${({ active }) => (active ? "#08345c" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#08345c")};
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  font-family: "Bungee", sans-serif;
  &:hover {
    transform: scale(1.05);
  }
  @media (max-width: 480px) {
    padding: 0.5rem 1.2rem;
    font-size: 0.85rem;
  }
  @media (max-width: 360px) {
    padding: 0.4rem 1rem;
    font-size: 0.75rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  border-radius: 30px 30px 0 0;
  background: linear-gradient(135deg, #f5a623, #f7c948);
  color: #08345c;
  font-family: "Bungee", sans-serif;
  font-size: 0.95rem;
  border: 2px solid #08345c;
  border-bottom: none;
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 0.5rem 0.8rem;
    border-radius: 20px 20px 0 0;
  }
  @media (max-width: 360px) {
    font-size: 0.6rem;
    padding: 0.4rem 0.5rem;
  }
`;

const RankCol = styled.div`
  width: 3.5rem;
  text-align: center;
  flex-shrink: 0;
  min-width: 2.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 360px) {
    width: 2.5rem;
    min-width: 2rem;
  }
`;

const NameCol = styled.div`
  flex: 2;
  padding-left: 0.5rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-family: "Bungee", sans-serif;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 360px) {
    padding-left: 0.2rem;
    gap: 0.2rem;
  }
`;

const ScoreCol = styled.div`
  width: 5rem;
  text-align: center;
  font-weight: 700;
  flex-shrink: 0;
  font-family: "Bungee", sans-serif;
  color: #08345c;
  min-width: 4rem;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 480px) {
    width: 3.5rem;
    min-width: 3rem;
  }
  @media (max-width: 360px) {
    width: 2.8rem;
    min-width: 2.5rem;
    font-size: 0.7rem;
  }
`;

const DateCol = styled.div`
  width: 6rem;
  text-align: right;
  font-size: 0.75rem;
  color: #6c757d;
  flex-shrink: 0;
  font-family: "Bungee", sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 480px) {
    width: 4.5rem;
    font-size: 0.6rem;
  }
  @media (max-width: 360px) {
    width: 3.5rem;
    font-size: 0.5rem;
  }
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  border: 2px solid #08345c;
  border-top: none;
  border-radius: 0 0 20px 20px;
  overflow: hidden;
  background: white;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.6rem 1.5rem;
  background: ${({ index }) => (index % 2 === 0 ? "#f0f6ff" : "#ffffff")};
  border-bottom: 1px solid #d0e0f0;
  &:last-child {
    border-bottom: none;
  }
  transition: transform 0.15s;
  &:hover {
    transform: scale(1.01);
  }
  font-family: "Bungee", sans-serif;
  @media (max-width: 480px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
  @media (max-width: 360px) {
    padding: 0.3rem 0.5rem;
    font-size: 0.65rem;
  }
`;

const RankBadge = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.8rem;
  color: white;
  background: ${({ rank }) => {
    if (rank === 1) return "#FFD700";
    if (rank === 2) return "#C0C0C0";
    if (rank === 3) return "#CD7F32";
    return "#6c757d";
  }};
  margin: 0 auto;
  font-family: "Bungee", sans-serif;
  @media (max-width: 360px) {
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.6rem;
  }
`;

const MedalEmoji = styled.span`
  font-size: 1.2rem;
  @media (max-width: 480px) {
    font-size: 1rem;
  }
  @media (max-width: 360px) {
    font-size: 0.8rem;
  }
`;

const NoScores = styled.p`
  text-align: center;
  color: #6c757d;
  padding: 2rem 0;
  font-size: 1.1rem;
  font-family: "Bungee", sans-serif;
  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
  @media (max-width: 360px) {
    font-size: 0.8rem;
    padding: 1rem 0;
  }
`;

const PageHeaderWithTrophy = styled(PageHeader)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: 360px) {
    font-size: 1.8rem;
  }
`;


const BackArrowSvg = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
    <circle cx="5" cy="12" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);

const BackArrow = styled.div`
  position: absolute;
  top: 2vh;
  left: 2vw;
  width: 2.8rem;
  height: 2.8rem;
  cursor: pointer;
  color: #08345c;
  z-index: 10;
  transition: transform 0.15s;
  &:hover {
    transform: scale(1.2);
  }
  @media (max-width: 480px) {
    width: 2.2rem;
    height: 2.2rem;
    top: 1.5vh;
    left: 3vw;
  }
  @media (max-width: 360px) {
    width: 1.8rem;
    height: 1.8rem;
  }
`;


const getRankMedal = (rank) => {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return null;
};

const LeaderboardScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState("home");
  const [scores, setScores] = useState([]);
  const [isMobileScreen, setIsMobileScreen] = useState(
    window.matchMedia("screen and (max-width: 768px)").matches
  );

  const locations = ["home", "school", "park"];

  useEffect(() => {
    const allScores = loadScores();
    const filtered = allScores
      .filter((s) => s.game === "waste-sorting" && s.type === activeType)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
    setScores(filtered);
  }, [activeType]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileScreen(window.matchMedia("screen and (max-width: 768px)").matches);
    };
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <LeaderboardWrapper>
      {!isMobileScreen && <SC.Jellyfish />}
      {!isMobileScreen && <SC.Scallop />}
      {!isMobileScreen && <SC.Algae1 />}
      {isMobileScreen && <SC.Algae2 />}
      {!isMobileScreen && <SC.Algae3 />}
      {!isMobileScreen && <SC.Crab />}
      {!isMobileScreen && <SC.Seahorse />}
      {!isMobileScreen && <SC.RedFish />}
      {!isMobileScreen && <ResponsiveSvg SvgComponent={SC.Wave2} />}
      {!isMobileScreen && <ResponsiveSvg SvgComponent={SC.Wave3} />}
      {!isMobileScreen && <ResponsiveSvg SvgComponent={SC.Wave4} />}
      {isMobileScreen && <ResponsiveSvg SvgComponent={SC.Wave5} />}

      <ScrollableContent>
        <BackArrow onClick={() => navigate("/results?game=waste-sorting&type=home")}>
          <BackArrowSvg />
        </BackArrow>

        <PageHeaderWithTrophy>🏆 {t("leaderboard.title")}</PageHeaderWithTrophy>
        <Spacer />

        <Card>
          <FilterTabs>
            {locations.map((loc) => (
              <Tab
                key={loc}
                active={activeType === loc}
                onClick={() => setActiveType(loc)}
              >
                {t(`gameTypes.${loc}`)}
              </Tab>
            ))}
          </FilterTabs>

          {scores.length > 0 ? (
            <>
              <HeaderRow>
                <RankCol>🏅</RankCol>
                <NameCol>👤 {t("leaderboard.name")}</NameCol>
                <ScoreCol>⭐ {t("leaderboard.score")}</ScoreCol>
                <DateCol>📅 {t("leaderboard.date")}</DateCol>
              </HeaderRow>

              <ListContainer>
                {scores.map((entry, index) => {
                  const rank = index + 1;
                  const medal = getRankMedal(rank);
                  return (
                    <ScoreRow key={entry.id || index} index={index}>
                      <RankCol>
                        <RankBadge rank={rank}>{rank}</RankBadge>
                      </RankCol>
                      <NameCol>
                        {medal && <MedalEmoji>{medal}</MedalEmoji>}
                        {entry.name || "Player"}
                      </NameCol>
                      <ScoreCol>{entry.score}</ScoreCol>
                      <DateCol>{entry.date}</DateCol>
                    </ScoreRow>
                  );
                })}
              </ListContainer>
            </>
          ) : (
            <NoScores>{t("leaderboard.noScores")}</NoScores>
          )}
        </Card>

        <Spacer />
      </ScrollableContent>
    </LeaderboardWrapper>
  );
};

export default LeaderboardScreen;